<script setup>
import { ref, onMounted, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useNotify } from '@/composables/notify'
import { useStepper } from '@/composables/stepper'
import { useIsUpdated, useClone } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { truncateStart } from '@/lib/utils'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import CameraGalleryInput from '@/components/shared/CameraGalleryInput.vue'
import ContactReferenceEdit from '@/components/shared/ContactReferenceEdit.vue'
import ListItem from '@/components/shared/ListItem.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { step, nextStep, prevStep, markAttempted, wasAttempted } = useStepper()
const { notifySuccess } = useNotify()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()

const { referenceTypeEnum } = planningStore
const referenceTypeMeta = portalStore.getLocalizedReferenceTypes()
const metaFor = (type) => referenceTypeMeta.find(m => m.id === type)

const form = ref(null)
const cameraInput = ref(null)
const editItem = ref({ references: [] })
const showDeleteDialog = ref(false)
const { isUpdated, isUpdating, setOriginal } = useIsUpdated(editItem)

const referenceEditItem = ref(null)
const editingReferenceIndex = ref(null)

const emit = defineEmits(['update', 'cancel'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))

onMounted(() => {
  editItem.value = useClone(props.modelValue)
  if (!editItem.value.references) editItem.value.references = []
  setOriginal(editItem.value)
})

const step1HasError = computed(() => !editItem.value.name)

const openReferenceEditor = (index = null) => {
  editingReferenceIndex.value = index
  referenceEditItem.value = index !== null
    ? useClone(editItem.value.references[index])
    : { reference_type: referenceTypeEnum.PHONE, label: '', value: '', is_default: editItem.value.references.length === 0 }
}

const onReferenceSaved = (reference) => {
  if (reference.is_default) {
    editItem.value.references
      .filter((r, i) => i !== editingReferenceIndex.value && r.reference_type === reference.reference_type)
      .forEach(r => { r.is_default = false })
  }
  if (editingReferenceIndex.value !== null) {
    editItem.value.references.splice(editingReferenceIndex.value, 1, reference)
  } else {
    editItem.value.references.push(reference)
  }
  referenceEditItem.value = null
}

const removeReference = (index) => {
  editItem.value.references.splice(index, 1)
}

const getReferenceCaptionChips = (reference) => [
  reference.is_default ? { label: i18n.t('common.default'), color: 'primary', textColor: 'white', size: 'sm' } : null,
].filter(Boolean)

const onSave = async() => {
  const valid = await form.value.validate()
  if (!valid || step1HasError.value) return
  isUpdating.value = true
  const updateItem = { ...editItem.value }
  if (!editItem.value.avatar_file?.payload) delete updateItem.avatar_file
  try {
    const { data: saved } = await planningStore.upsertContactAsync(updateItem)
    notifySuccess(editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    emit('update', saved)
  } finally {
    isUpdating.value = false
  }
}

const onDelete = async() => {
  await planningStore.deleteContactAsync(editItem.value.id)
  notifySuccess(i18n.t('info.deletedSuccessfully'))
  emit('update')
}
</script>

<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <q-dialog
      :maximized="isMobile"
      :model-value="referenceEditItem !== null"
      class="dialog-xs"
      @hide="referenceEditItem = null"
    >
      <contact-reference-edit
        v-if="referenceEditItem !== null"
        :model-value="referenceEditItem"
        @save="onReferenceSaved"
        @cancel="referenceEditItem = null"
      />
    </q-dialog>
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete"
    />
    <navigate-back
      :text="editItem?.id ? i18n.t('common.editContact') : i18n.t('common.addContact')"
      @cancel="emit('cancel')"
    />
    <q-card-section
      class="scroll col q-pa-none dialog-card-content-scroll"
      :class="{ 'dialog-card-content': editItem?.id }"
    >
      <q-card-section class="q-px-sm q-pt-none">
        <q-form ref="form">
          <q-stepper v-model="step" class="q-py-none">
            <q-step
              :name="1"
              :title="i18n.t('common.basicInfo')"
              :icon="wasAttempted(1) && step1HasError ? 'mdi-alert' : 'mdi-folder-plus'"
              :done="!!editItem.id || step > 1"
              :header-nav="!!editItem.id || step > 1"
              :error="wasAttempted(1) && step1HasError"
              error-color="negative"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.name')"
                  autofocus
                  :rules="[val => !!val || i18n.t('common.required')]"
                />
                <q-input
                  v-model="editItem.surname"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.lastName')"
                />
                <q-input
                  v-model="editItem.role"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.role')"
                />
                <q-checkbox
                  v-model="editItem.is_default"
                  class="col-12"
                  :label="i18n.t('company.defaultContact')"
                  color="primary"
                />
              </div>
              <stepper-navigation
                :continue="() => { markAttempted(1); form.validate().then((success) => !step1HasError && success && nextStep()) }"
              />
            </q-step>
            <q-step
              :name="2"
              :title="i18n.t('common.picture')"
              icon="mdi-camera"
              :done="!!editItem.id || step > 2"
              :header-nav="!!editItem.id || step > 2"
            >
              <div class="column items-center q-py-md" style="min-height: 10rem;">
                <camera-gallery-input ref="cameraInput" :edit-item="editItem" />
                <q-btn
                  v-if="!editItem.avatar_file && !editItem.avatar_url"
                  icon="mdi-camera"
                  size="2rem"
                  round
                  @click.stop="cameraInput.onActivateCamera()"
                />
                <q-avatar
                  v-else
                  size="10rem"
                  class="cursor-pointer"
                  @click.stop="cameraInput.onActivateCamera()"
                >
                  <q-img
                    loading="lazy"
                    fit="cover"
                    :src="editItem.avatar_file?.payload || editItem.avatar_file || editItem.avatar_url"
                  >
                    <div class="absolute-full text-subtitle2 flex flex-center image-camera">
                      <q-icon name="mdi-camera" size="2rem" />
                    </div>
                  </q-img>
                </q-avatar>
              </div>
              <stepper-navigation :back="prevStep" :continue="nextStep" />
            </q-step>
            <q-step
              :name="3"
              :title="i18n.t('common.reference', 2)"
              icon="mdi-card-account-phone-outline"
              :done="!!editItem.id || step > 3"
              :header-nav="!!editItem.id || step > 3"
            >
              <div>
                <div class="row items-center justify-end q-mb-sm">
                  <div class="flex flex-center" style="width: 3.5rem;">
                    <q-icon
                      name="mdi-plus-circle-outline"
                      color="positive"
                      class="cursor-pointer"
                      @click="openReferenceEditor()"
                    />
                  </div>
                </div>
                <div v-if="!editItem.references.length" class="text-grey-6 text-caption q-pa-md text-center">
                  {{ i18n.t('common.noData') }}
                </div>
                <q-list v-else>
                  <list-item
                    v-for="(reference, index) in editItem.references"
                    :key="index"
                    clickable
                    border
                    :border-color="metaFor(reference.reference_type)?.color"
                    class="q-pa-sm reference-item rounded-borders"
                    :avatar-icon="metaFor(reference.reference_type)?.icon"
                    :avatar-icon-color="metaFor(reference.reference_type)?.color"
                    :label="truncateStart(reference.value, 30)"
                    :caption="reference.label"
                    :caption-chips="getReferenceCaptionChips(reference)"
                    icon="mdi-minus-circle-outline"
                    icon-color="negative"
                    @click="openReferenceEditor(index)"
                    @icon-click="removeReference(index)"
                  />
                </q-list>
              </div>
              <stepper-navigation
                :back="prevStep"
                :finish="editItem.id ? null : () => onSave()"
                :finish-label="i18n.t('common.addContact')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
        </q-form>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="editItem?.id" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="showDeleteDialog = true"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="onSave"
      />
    </q-card-actions>
  </q-card>
</template>

<style scoped>
.reference-item {
  border: 1px solid var(--q-separator-color, #e0e0e0);
}
.reference-item + .reference-item {
  margin-top: .5rem;
}
</style>

